import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Activity, ChevronDown, Eye, LockKeyhole, Printer,
  Plus, Save, Search, ShieldCheck, Trash2, X,
} from 'lucide-react';
import { saveDokterUmumNote, loadPharmacyDrugs, loadClientVisitHistory } from './data.js';
import { ageFromBirthDate, dateLabel, friendlyError } from './lib/format.js';

// ── ICD-10 list ───────────────────────────────────────────────────────────────
const ICD10_UMUM = [
  { code: 'J06.9',  display: 'ISPA, tidak spesifik' },
  { code: 'J00',    display: 'Nasofaringitis akut (Common cold)' },
  { code: 'J02.9',  display: 'Faringitis akut, tidak spesifik' },
  { code: 'J03.9',  display: 'Tonsilitis akut, tidak spesifik' },
  { code: 'J18.9',  display: 'Pneumonia, tidak spesifik' },
  { code: 'A09',    display: 'Diare dan gastroenteritis infeksius' },
  { code: 'A90',    display: 'Demam dengue' },
  { code: 'A91',    display: 'Demam berdarah dengue (DBD)' },
  { code: 'B34.9',  display: 'Infeksi virus, tidak spesifik' },
  { code: 'I10',    display: 'Hipertensi esensial (primer)' },
  { code: 'E11.9',  display: 'Diabetes melitus tipe 2, tanpa komplikasi' },
  { code: 'E10.9',  display: 'Diabetes melitus tipe 1, tanpa komplikasi' },
  { code: 'E03.9',  display: 'Hipotiroidisme, tidak spesifik' },
  { code: 'E78.5',  display: 'Hiperlipidemia, tidak spesifik' },
  { code: 'E66.9',  display: 'Obesitas, tidak spesifik' },
  { code: 'K29.7',  display: 'Gastritis, tidak spesifik' },
  { code: 'K21.0',  display: 'GERD dengan esofagitis' },
  { code: 'G43.9',  display: 'Migrain, tidak spesifik' },
  { code: 'G44.2',  display: 'Tension-type headache' },
  { code: 'R51',    display: 'Nyeri kepala' },
  { code: 'M54.5',  display: 'Nyeri punggung bawah (LBP)' },
  { code: 'M54.2',  display: 'Nyeri leher (Cervikal)' },
  { code: 'M25.5',  display: 'Nyeri sendi' },
  { code: 'N39.0',  display: 'ISK, lokasi tidak spesifik' },
  { code: 'R50.9',  display: 'Demam, tidak spesifik' },
  { code: 'R05',    display: 'Batuk' },
  { code: 'R06.0',  display: 'Sesak napas (Dyspnea)' },
  { code: 'R10.4',  display: 'Nyeri perut, tidak spesifik' },
  { code: 'R11',    display: 'Mual dan muntah' },
  { code: 'R42',    display: 'Pusing (Dizziness)' },
  { code: 'Z00.0',  display: 'Pemeriksaan medis umum (MCU)' },
  { code: 'Z30.0',  display: 'Konseling kontrasepsi' },
];

function ICD10Picker({ value, onChange, multiple = false, readOnly, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const filtered = ICD10_UMUM.filter((item) => {
    const q = query.toLowerCase();
    return !q || item.code.toLowerCase().includes(q) || item.display.toLowerCase().includes(q);
  }).slice(0, 12);
  function select(item) {
    if (multiple) {
      const cur = Array.isArray(value) ? value : [];
      if (!cur.find((v) => v.code === item.code)) onChange([...cur, item]);
    } else { onChange(item); }
    setQuery(''); setOpen(false);
  }
  function remove(code) {
    if (multiple) onChange((Array.isArray(value) ? value : []).filter((v) => v.code !== code));
    else onChange(null);
  }
  const tags = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
  return (
    <div className="icd-picker">
      {tags.length > 0 && (
        <div className="icd-tags">
          {tags.map((v) => (
            <span key={v.code} className="icd-tag">
              <code>{v.code}</code>{v.display}
              {!readOnly && <button type="button" className="icd-tag-remove" onClick={() => remove(v.code)}>×</button>}
            </span>
          ))}
        </div>
      )}
      {!readOnly && (!value || multiple) && (
        <div className="icd-search-wrap">
          <Search size={13} className="icd-search-icon" />
          <input className="icd-search-input" value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 180)}
            placeholder={placeholder || (multiple ? 'Tambah kode ICD-10…' : 'Cari kode ICD-10…')}
          />
          {open && filtered.length > 0 && (
            <div className="icd-dropdown">
              {filtered.map((item) => (
                <div key={item.code} className="icd-option" onMouseDown={() => select(item)}>
                  <code>{item.code}</code><span>{item.display}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!readOnly && value && !multiple && (
        <button type="button" className="icd-clear" onClick={() => onChange(null)}>Ganti kode</button>
      )}
    </div>
  );
}

// ── Prescription ──────────────────────────────────────────────────────────────
const EMPTY_RX = { drug_name: '', dose: '', frequency: '', duration_days: '', qty: '', route: 'Oral', notes: '' };
const ROUTE_OPTS = ['Oral', 'Topikal', 'Injeksi IM', 'Injeksi IV', 'Sublingual', 'Inhalasi', 'Suppositoria', 'Tetes mata', 'Tetes telinga'];

function DrugAutocomplete({ value, onChange, catalog, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || '');
  const ref = useRef(null);
  useEffect(() => { setQ(value || ''); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const matches = q.length >= 2 ? catalog.filter((d) => d.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];
  return (
    <div className="rx-drug-wrap" ref={ref}>
      <input className="rx-input rx-drug" value={q} placeholder="Nama obat *" disabled={disabled}
        onChange={(e) => { setQ(e.target.value); onChange(e.target.value, ''); setOpen(true); }}
        onFocus={() => q.length >= 2 && setOpen(true)} autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul className="rx-drug-dropdown">
          {matches.map((d) => (
            <li key={d.id} className="rx-drug-option" onMouseDown={() => { setQ(d.name); onChange(d.name, d.unit || ''); setOpen(false); }}>
              <span className="rx-drug-name">{d.name}</span>
              {d.unit && <span className="rx-drug-unit">{d.unit}</span>}
              {d.category && <span className="rx-drug-cat">{d.category}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PrescriptionBuilder({ value, onChange, readOnly, drugCatalog = [] }) {
  const rows = Array.isArray(value) ? value : [];
  return (
    <div className="rx-builder">
      {rows.length === 0 && <p className="rx-empty">Belum ada resep.</p>}
      {rows.map((row, i) => (
        <div key={i} className="rx-row">
          <div className="rx-row-num">{i + 1}</div>
          <div className="rx-fields">
            <DrugAutocomplete value={row.drug_name} catalog={drugCatalog} disabled={readOnly}
              onChange={(name, unit) => {
                const updated = { ...rows[i], drug_name: name };
                if (unit && !rows[i].unit) updated.unit = unit;
                onChange(rows.map((r, idx) => idx === i ? updated : r));
              }}
            />
            <input className="rx-input rx-dose" value={row.dose} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,dose:e.target.value}:r))} placeholder="Dosis" disabled={readOnly} />
            <input className="rx-input rx-freq" value={row.frequency} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,frequency:e.target.value}:r))} placeholder="3×1" disabled={readOnly} />
            <input className="rx-input rx-dur" type="number" value={row.duration_days} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,duration_days:e.target.value}:r))} placeholder="Hari" disabled={readOnly} />
            <input className="rx-input rx-qty" type="number" value={row.qty} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,qty:e.target.value}:r))} placeholder="Jml" disabled={readOnly} />
            <select className="rx-input rx-route" value={row.route} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,route:e.target.value}:r))} disabled={readOnly}>
              {ROUTE_OPTS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input className="rx-input rx-notes" value={row.notes} onChange={(e) => onChange(rows.map((r,idx)=>idx===i?{...r,notes:e.target.value}:r))} placeholder="Catatan" disabled={readOnly} />
          </div>
          {!readOnly && <button type="button" className="rx-remove" onClick={() => onChange(rows.filter((_,idx)=>idx!==i))}><Trash2 size={14}/></button>}
        </div>
      ))}
      {!readOnly && (
        <button type="button" className="button button-soft rx-add" onClick={() => onChange([...rows, { ...EMPTY_RX }])}>
          <Plus size={15}/> Tambah Obat
        </button>
      )}
    </div>
  );
}

const nullable = (v) => v ?? '';

// ── History Tab ───────────────────────────────────────────────────────────────
const HIST_TABS = ['Riwayat','Vital','Alergi','Terapi','Obat','ICD-10','Berkas','Chat'];

// ── Main Modal ────────────────────────────────────────────────────────────────
export function DokterUmumFormModal({ data, onClose, onSaved, onGoToAction }) {
  const note   = data.dokter_umum_note || {};
  const visit  = data.visit;
  const client = data.client;
  const isExistingFinal = /^(FINAL|AMENDED)$/i.test(note.draft_status || '');
  const matchingStaff = data.staff?.find((r) => r.id === note.nakes_id)
    || data.staff?.find((r) => r.name === visit.doctor_name);

  const [form, setForm] = useState({
    nakes_id:             matchingStaff?.id || '',
    current_complaint:    nullable(note.current_complaint),
    illness_history:      nullable(note.illness_history),
    past_medical_history: nullable(note.past_medical_history),
    family_history:       nullable(note.family_history),
    allergy:              nullable(note.allergy),
    current_medications:  nullable(note.current_medications),
    consciousness:        nullable(note.consciousness) || 'Compos Mentis',
    vital_signs: {
      blood_pressure:   nullable(note.vital_signs?.blood_pressure),
      bpm:              nullable(note.vital_signs?.bpm),
      temperature:      nullable(note.vital_signs?.temperature),
      respiratory_rate: nullable(note.vital_signs?.respiratory_rate),
      spo2:             nullable(note.vital_signs?.spo2),
      weight_kg:        nullable(note.vital_signs?.weight_kg),
      height_cm:        nullable(note.vital_signs?.height_cm),
    },
    physical_exam: {
      general:     nullable(note.physical_exam?.general),
      head_neck:   nullable(note.physical_exam?.head_neck),
      thorax:      nullable(note.physical_exam?.thorax),
      abdomen:     nullable(note.physical_exam?.abdomen),
      extremities: nullable(note.physical_exam?.extremities),
      other:       nullable(note.physical_exam?.other),
    },
    diagnosis:              nullable(note.diagnosis),
    icd10_primary:          note.icd10_primary  || null,
    icd10_secondary:        note.icd10_secondary || [],
    differential_diagnosis: nullable(note.differential_diagnosis),
    prescriptions:          Array.isArray(note.prescriptions) ? note.prescriptions : [],
    lab_requests:           nullable(note.lab_requests),
    referral_notes:         nullable(note.referral_notes),
    follow_up:              nullable(note.follow_up),
    action_notes:           nullable(note.action_notes),
    education_notes:        nullable(note.education_notes),
  });

  const [correctionMode, setCorrectionMode] = useState(!isExistingFinal);
  const [busy, setBusy]       = useState('');
  const [error, setError]     = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [drugCatalog, setDrugCatalog]   = useState([]);
  const [history, setHistory]           = useState([]);
  const [histTab, setHistTab]           = useState('Riwayat');
  const [histOnlyMe, setHistOnlyMe]     = useState(false);
  const [showMoreAnamnesa, setShowMoreAnamnesa] = useState(false);
  const [showExamDetail,   setShowExamDetail]   = useState(false);
  const readOnly = isExistingFinal && !correctionMode;

  // BMI
  const bmi = (() => {
    const w = parseFloat(form.vital_signs.weight_kg);
    const h = parseFloat(form.vital_signs.height_cm) / 100;
    return w > 0 && h > 0 ? (w / (h * h)).toFixed(1) : '';
  })();

  useEffect(() => { loadPharmacyDrugs().then(setDrugCatalog).catch(() => {}); }, []);
  useEffect(() => {
    if (client?.id) loadClientVisitHistory(client.id).then(setHistory).catch(() => {});
  }, [client?.id]);

  const update      = useCallback((k, v) => setForm((c) => ({ ...c, [k]: v })), []);
  const updateVital = useCallback((k, v) => setForm((c) => ({ ...c, vital_signs: { ...c.vital_signs, [k]: v } })), []);
  const updateExam  = useCallback((k, v) => setForm((c) => ({ ...c, physical_exam: { ...c.physical_exam, [k]: v } })), []);

  function validate(finalize) {
    if (finalize && !form.nakes_id) return 'Dokter pemeriksa wajib dipilih.';
    if (finalize && !form.current_complaint.trim()) return 'Keluhan utama wajib diisi.';
    if (finalize && !form.diagnosis.trim()) return 'Diagnosis wajib diisi.';
    if (finalize && !form.icd10_primary?.code) return 'Kode ICD-10 primer wajib dipilih.';
    const bp = form.vital_signs.blood_pressure.trim();
    if (bp && !/^\d{2,3}\s*\/\s*\d{2,3}$/.test(bp)) return 'Tekanan darah harus format 120/80.';
    return '';
  }

  async function submit(finalize) {
    const err = validate(finalize);
    setError(err);
    if (err) return;
    if (finalize && !window.confirm(
      isExistingFinal ? 'Finalisasi koreksi rekam medis?' : 'Finalisasi pemeriksaan? Pastikan data sudah lengkap.'
    )) return;
    setBusy(finalize ? 'final' : 'draft');
    setSavedMsg('');
    try {
      const educationNote = form.education_notes?.trim();
      const actionNotes = [
        form.action_notes?.trim(),
        educationNote ? `Edukasi / Rekomendasi: ${educationNote}` : '',
      ].filter(Boolean).join('\n\n');
      const payload = {
        ...form,
        action_notes: actionNotes,
        vital_signs: { ...form.vital_signs, bmi },
        icd10_primary: form.icd10_primary || null,
      };
      const result = await saveDokterUmumNote(visit.id, payload, finalize);
      if (finalize) {
        // Finalisasi → tutup form dan refresh daftar
        onSaved(result, isExistingFinal ? 'Koreksi berhasil difinalkan.' : 'Pemeriksaan berhasil difinalkan.');
      } else {
        // Draft → tetap di form, tampilkan pesan sukses inline
        setSavedMsg('✓ Draft berhasil disimpan — ' + new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }));
        setTimeout(() => setSavedMsg(''), 4000);
      }
    } catch (e) { setError(friendlyError(e)); }
    finally { setBusy(''); }
  }

  const doctorName = (data.staff || []).find((s) => s.id === form.nakes_id)?.name || visit.doctor_name || '';
  const visibleHistory = histOnlyMe
    ? history.filter((h) => h.nakes_id === form.nakes_id || h.doctor_name === doctorName)
    : history;

  const inp = { padding: '7px 10px', border: '1px solid #c9cfd8', borderRadius: 5, fontSize: 13, background: '#fff', width: '100%', boxSizing: 'border-box' };
  const ta  = { ...inp, resize: 'vertical', fontFamily: 'inherit' };

  return (
    <div className="emr-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="emr-shell" role="dialog" aria-modal="true">

        {/* ── Top bar: patient info ── */}
        <div className="emr-topbar">
          <div className="emr-topbar-info">
            <span><b>PASIEN:</b> {client.name}</span>
            <span className="emr-sep">·</span>
            <span><b>UMUR:</b> {ageFromBirthDate(client.birth_date)}</span>
            <span className="emr-sep">·</span>
            <span><b>JK:</b> {client.gender === 'Laki-laki' ? 'Pria' : client.gender === 'Perempuan' ? 'Wanita' : client.gender || '—'}</span>
            <span className="emr-sep">·</span>
            <span><b>ASURANSI:</b> {client.insurance_type || 'Pribadi'}</span>
            {note.draft_status && (
              <span className={`emr-status-badge emr-status-${(note.draft_status||'').toLowerCase()}`}>
                {note.draft_status}
              </span>
            )}
          </div>
          {savedMsg && (
            <span className="emr-saved-msg">{savedMsg}</span>
          )}
          <div className="emr-topbar-actions">
            {readOnly ? (
              <button className="emr-btn emr-btn-primary" onClick={onGoToAction} disabled={!!busy}>
                <Activity size={15}/> Tindakan
              </button>
            ) : (
              <>
                <button className="emr-btn emr-btn-soft" onClick={() => submit(false)} disabled={!!busy}>
                  <Save size={15}/> {busy === 'draft' ? 'Menyimpan…' : 'Simpan'}
                </button>
                <button className="emr-btn emr-btn-success" onClick={() => submit(true)} disabled={!!busy}>
                  <ShieldCheck size={15}/> {busy === 'final' ? 'Memfinalkan…' : (isExistingFinal ? 'Finalisasi Koreksi' : 'FINAL')}
                </button>
              </>
            )}
            <button className="emr-btn emr-btn-close" onClick={onClose} disabled={!!busy}>
              <X size={15}/> Tutup
            </button>
          </div>
        </div>

        {/* ── Meta bar: visit details ── */}
        <div className="emr-metabar">
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">TANGGAL</span>
            <span className="emr-meta-val">{dateLabel(visit.visit_date)}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">JENIS PASIEN</span>
            <span className="emr-meta-val">Rawat Jalan</span>
          </div>
          <div className="emr-meta-item emr-meta-doctor">
            <span className="emr-meta-lbl">TENAGA MEDIS</span>
            <select className="emr-doctor-select" value={form.nakes_id}
              onChange={(e) => update('nakes_id', e.target.value)}
              disabled={readOnly || !!busy}
            >
              <option value="">— Pilih —</option>
              {(data.staff || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">POLIKLINIK</span>
            <span className="emr-meta-val">Dokter Umum</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">TUJUAN KUNJUNGAN</span>
            <span className="emr-meta-val">{visit.visit_purpose || 'Pemeriksaan Umum'}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">RUANGAN</span>
            <span className="emr-meta-val">{visit.room_name || 'Tidak ditentukan'}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">ALERGI</span>
            <span className="emr-meta-val emr-meta-alergi">{form.allergy || 'Tidak ada'}</span>
          </div>
          <div className="emr-meta-item">
            <span className="emr-meta-lbl">OBAT AKTIF</span>
            <span className="emr-meta-val">{form.current_medications || '—'}</span>
          </div>
        </div>

        {/* ── Alerts ── */}
        {(error || isExistingFinal) && (
          <div className="emr-alerts">
            {error && <div className="alert alert-danger">{error}</div>}
            {isExistingFinal && !correctionMode && (
              <div className="alert alert-info clinical-lock-notice" style={{ margin: 0 }}>
                <LockKeyhole size={16}/>
                <span>Rekam medis sudah {note.draft_status}. Data baca-saja.</span>
                {data.can_correct_final && (
                  <button className="button button-mini button-quiet" onClick={() => setCorrectionMode(true)}>Mulai koreksi</button>
                )}
              </div>
            )}
            {isExistingFinal && correctionMode && (
              <div className="alert alert-warning clinical-lock-notice" style={{ margin: 0 }}>
                <ShieldCheck size={16}/>
                <span>Mode koreksi aktif. Versi sebelumnya tersimpan di audit.</span>
              </div>
            )}
          </div>
        )}

        {/* ── Body: split panel ── */}
        <div className="emr-body">

          {/* LEFT: form */}
          <div className="emr-form-panel" role="main">
            <fieldset disabled={readOnly || !!busy} style={{ border: 'none', padding: 0, margin: 0 }}>

              {/* KELUHAN UTAMA */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">KELUHAN UTAMA</span>
                </div>
                <textarea style={ta} rows={3} value={form.current_complaint}
                  onChange={(e) => update('current_complaint', e.target.value)}
                  placeholder="Keluhan utama pasien… (akhiri kalimat dengan Enter)"
                  maxLength={3000}
                />

                {/* Inline vital - TD & DN */}
                <div className="emr-vitals-inline">
                  <div className="emr-vital-pair">
                    <label>TD</label>
                    <input style={{ ...inp, width: 90 }} value={form.vital_signs.blood_pressure}
                      onChange={(e) => updateVital('blood_pressure', e.target.value)} placeholder="120/80" maxLength={9}/>
                    <span>mmHg</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>DN</label>
                    <input style={{ ...inp, width: 60 }} type="number" min="20" max="300" value={form.vital_signs.bpm}
                      onChange={(e) => updateVital('bpm', e.target.value)} placeholder="80"/>
                    <span>/mnt</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>Suhu</label>
                    <input style={{ ...inp, width: 65 }} type="number" min="30" max="45" step="0.1" value={form.vital_signs.temperature}
                      onChange={(e) => updateVital('temperature', e.target.value)} placeholder="36.5"/>
                    <span>°C</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>SpO₂</label>
                    <input style={{ ...inp, width: 60 }} type="number" min="50" max="100" value={form.vital_signs.spo2}
                      onChange={(e) => updateVital('spo2', e.target.value)} placeholder="98"/>
                    <span>%</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>RR</label>
                    <input style={{ ...inp, width: 60 }} type="number" min="5" max="60" value={form.vital_signs.respiratory_rate}
                      onChange={(e) => updateVital('respiratory_rate', e.target.value)} placeholder="18"/>
                    <span>×/mnt</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>BB</label>
                    <input style={{ ...inp, width: 65 }} type="number" step="0.1" value={form.vital_signs.weight_kg}
                      onChange={(e) => updateVital('weight_kg', e.target.value)} placeholder="65"/>
                    <span>kg</span>
                  </div>
                  <div className="emr-vital-pair">
                    <label>TB</label>
                    <input style={{ ...inp, width: 65 }} type="number" value={form.vital_signs.height_cm}
                      onChange={(e) => updateVital('height_cm', e.target.value)} placeholder="165"/>
                    <span>cm</span>
                  </div>
                  {bmi && (
                    <div className="emr-vital-pair">
                      <label>BMI</label>
                      <span className="emr-bmi-val">{bmi}</span>
                    </div>
                  )}
                </div>

                {/* Riwayat penyakit sekarang (collapsed by default) */}
                <button type="button" className="emr-expand-btn" onClick={() => setShowMoreAnamnesa(!showMoreAnamnesa)}>
                  <ChevronDown size={14} className={showMoreAnamnesa ? 'emr-chevron-open' : ''}/> Anamnesa lengkap
                </button>
                {showMoreAnamnesa && (
                  <div className="emr-extra-fields">
                    <label className="emr-extra-label">Riwayat penyakit sekarang</label>
                    <textarea style={ta} rows={2} value={form.illness_history}
                      onChange={(e) => update('illness_history', e.target.value)}
                      placeholder="Perkembangan keluhan, faktor pemberat/pereda…" maxLength={3000}/>
                    <label className="emr-extra-label">Riwayat penyakit dahulu</label>
                    <textarea style={ta} rows={2} value={form.past_medical_history}
                      onChange={(e) => update('past_medical_history', e.target.value)}
                      placeholder="Penyakit kronis, operasi, rawat inap sebelumnya…" maxLength={2000}/>
                    <label className="emr-extra-label">Riwayat keluarga</label>
                    <textarea style={ta} rows={2} value={form.family_history}
                      onChange={(e) => update('family_history', e.target.value)}
                      placeholder="Penyakit herediter pada keluarga inti…" maxLength={2000}/>
                    <label className="emr-extra-label">Alergi obat &amp; makanan</label>
                    <textarea style={ta} rows={2} value={form.allergy}
                      onChange={(e) => update('allergy', e.target.value)}
                      placeholder="Nama zat dan reaksi yang pernah terjadi…" maxLength={1000}/>
                    <label className="emr-extra-label">Obat yang sedang dikonsumsi</label>
                    <textarea style={ta} rows={2} value={form.current_medications}
                      onChange={(e) => update('current_medications', e.target.value)}
                      placeholder="Nama obat, dosis, frekuensi saat ini…" maxLength={1000}/>
                  </div>
                )}
              </div>

              {/* CATATAN PEMERIKSAAN */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">CATATAN PEMERIKSAAN</span>
                </div>
                <textarea style={ta} rows={3} value={form.physical_exam.general}
                  onChange={(e) => updateExam('general', e.target.value)}
                  placeholder="Keadaan umum, status kesadaran, catatan pemeriksaan fisik…" maxLength={3000}/>
                <button type="button" className="emr-expand-btn" onClick={() => setShowExamDetail(!showExamDetail)}>
                  <ChevronDown size={14} className={showExamDetail ? 'emr-chevron-open' : ''}/> Detail pemeriksaan fisik
                </button>
                {showExamDetail && (
                  <div className="emr-extra-fields emr-exam-grid">
                    {[
                      { key: 'head_neck',   label: 'Kepala & Leher',          ph: 'Normosefal, faring hiperemis…' },
                      { key: 'thorax',      label: 'Thorax (jantung & paru)',  ph: 'BJ I-II reguler, vesikular…' },
                      { key: 'abdomen',     label: 'Abdomen',                  ph: 'Supel, nyeri tekan (-)…' },
                      { key: 'extremities', label: 'Ekstremitas',              ph: 'Akral hangat, edema (-)…' },
                      { key: 'other',       label: 'Lain-lain',                ph: 'Pemeriksaan tambahan…' },
                    ].map(({ key, label, ph }) => (
                      <div key={key} className="emr-exam-item">
                        <label>{label}</label>
                        <textarea style={{ ...ta, fontSize: 12 }} rows={2} value={form.physical_exam[key]}
                          onChange={(e) => updateExam(key, e.target.value)} placeholder={ph} disabled={readOnly}/>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* DIAGNOSA */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">DIAGNOSA</span>
                  <span className="emr-badge emr-badge-blue">ICD-10</span>
                </div>
                {!form.icd10_primary && !readOnly && (
                  <p className="emr-placeholder-hint">Belum ada Diagnosa Penyakit. Pilih dari daftar ICD-10 atau ketikkan diagnosa di bawah.</p>
                )}
                <ICD10Picker value={form.icd10_primary} onChange={(v) => update('icd10_primary', v)}
                  readOnly={readOnly} placeholder="Cari kode ICD-10 diagnosis utama…"/>
                {(form.icd10_secondary?.length > 0 || !readOnly) && (
                  <ICD10Picker value={form.icd10_secondary} onChange={(v) => update('icd10_secondary', v)}
                    multiple readOnly={readOnly} placeholder="Tambah diagnosis sekunder / komorbid…"/>
                )}
                <textarea style={{ ...ta, marginTop: 8 }} rows={2} value={form.diagnosis}
                  onChange={(e) => update('diagnosis', e.target.value)}
                  placeholder="Catatan Diagnosa (diagnosis banding, keterangan tambahan)…" maxLength={2000}/>
              </div>

              {/* PERENCANAAN */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label">PERENCANAAN</span>
                  <span className="emr-badge emr-badge-teal">TERAPI</span>
                  <span className="emr-badge emr-badge-orange">OBAT</span>
                </div>

                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">RESEP OBAT</div>
                  <PrescriptionBuilder value={form.prescriptions} onChange={(v) => update('prescriptions', v)}
                    readOnly={readOnly || !!busy} drugCatalog={drugCatalog}/>
                </div>

                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">PERENCANAAN OBAT / RESEP (FREE-TEXT)</div>
                  <textarea style={ta} rows={2} value={form.lab_requests}
                    onChange={(e) => update('lab_requests', e.target.value)}
                    placeholder="Catatan resep bebas, permintaan lab, penunjang diagnostik…" maxLength={2000}/>
                </div>

                <div className="emr-plan-subsection">
                  <div className="emr-plan-sub-label">PERENCANAAN PERAWATAN / TERAPI (FREE-TEXT)</div>
                  <textarea style={ta} rows={2} value={form.action_notes}
                    onChange={(e) => update('action_notes', e.target.value)}
                    placeholder="Rencana terapi, tindak lanjut, edukasi pasien…" maxLength={3000}/>
                </div>

                {form.referral_notes && (
                  <div className="emr-plan-subsection">
                    <div className="emr-plan-sub-label">CATATAN RUJUKAN</div>
                    <textarea style={ta} rows={2} value={form.referral_notes}
                      onChange={(e) => update('referral_notes', e.target.value)}
                      placeholder="Rujukan ke spesialis atau fasilitas lain…" maxLength={1000}/>
                  </div>
                )}
                {!form.referral_notes && !readOnly && (
                  <button type="button" className="emr-add-section-btn"
                    onClick={() => update('referral_notes', ' ')}>+ Tambah Catatan Rujukan</button>
                )}
              </div>

              {/* Edukasi */}
              <div className="emr-section">
                <div className="emr-section-head">
                  <span className="emr-section-label" style={{ color: '#0891b2' }}>Edukasi / Rekomendasi</span>
                  <small className="emr-hint-inline">akhiri tekan Enter</small>
                </div>
                <input style={inp} value={form.education_notes}
                  onChange={(e) => update('education_notes', e.target.value)}
                  placeholder="Edukasi, rekomendasi, dan instruksi untuk pasien…"/>
              </div>

              {/* Jadwal Kontrol */}
              <div className="emr-section emr-section-last">
                <div className="emr-section-head">
                  <span className="emr-section-label" style={{ color: '#0891b2' }}>Jadwal Kontrol</span>
                </div>
                <div className="emr-followup-row">
                  <input style={{ ...inp, width: 180 }} type="date" value={form.follow_up?.match(/\d{4}-\d{2}-\d{2}/)?.[0] || ''}
                    onChange={(e) => update('follow_up', e.target.value)}/>
                  <input style={{ ...inp, flex: 1 }} value={form.follow_up || ''}
                    onChange={(e) => update('follow_up', e.target.value)}
                    placeholder="atau ketikkan: 3 hari lagi / 1 minggu…"/>
                  <button type="button" className="emr-btn emr-btn-followup" disabled>FOLLOW-UP</button>
                </div>
              </div>

            </fieldset>
          </div>

          {/* RIGHT: history */}
          <div className="emr-history-panel">
            <div className="emr-hist-tabs">
              {HIST_TABS.map((t) => (
                <button key={t} type="button"
                  className={`emr-hist-tab ${histTab === t ? 'emr-hist-tab-active' : ''}`}
                  onClick={() => setHistTab(t)}>
                  {t}
                </button>
              ))}
            </div>

            <div className="emr-hist-body">
              {histTab === 'Riwayat' && (
                <>
                  <div className="emr-hist-title">
                    <span>RIWAYAT PERAWATAN</span>
                    <button type="button" className="emr-hist-print" title="Cetak riwayat">
                      <Printer size={14}/> RIWAYAT
                    </button>
                  </div>
                  {doctorName && (
                    <label className="emr-hist-filter">
                      <input type="checkbox" checked={histOnlyMe} onChange={(e) => setHistOnlyMe(e.target.checked)}/>
                      Hanya {doctorName}
                    </label>
                  )}
                  <div className="emr-hist-table-wrap">
                    <table className="emr-hist-table">
                      <thead>
                        <tr>
                          <th>TGL</th>
                          <th>KELUHAN</th>
                          <th>DIAGNOSA</th>
                          <th>PERAWATAN</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHistory.length === 0 && (
                          <tr><td colSpan={5} className="emr-hist-empty">Belum ada riwayat kunjungan.</td></tr>
                        )}
                        {visibleHistory.map((h) => {
                          const isCurrent = h.id === visit.id;
                          return (
                            <tr key={h.id} className={isCurrent ? 'emr-hist-row-current' : ''}>
                              <td className="emr-hist-date" style={{ whiteSpace: 'nowrap' }}>
                                {dateLabel(h.visit_date)}
                              </td>
                              <td className="emr-hist-cell">
                                {h.keluhan
                                  ? <span title={h.keluhan}>{h.keluhan.slice(0, 50)}{h.keluhan.length > 50 ? '…' : ''}</span>
                                  : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-cell">
                                {h.icd10?.code
                                  ? <code className="emr-hist-icd">{h.icd10.code}</code>
                                  : null}
                                {h.diagnosa
                                  ? <span title={h.diagnosa}>{h.diagnosa.slice(0, 40)}{h.diagnosa.length > 40 ? '…' : ''}</span>
                                  : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-cell">
                                {h.tindakan.length > 0
                                  ? h.tindakan.map((t, i) => <div key={i} className="emr-hist-tindakan">› {t}</div>)
                                  : h.catatan
                                    ? <span title={h.catatan}>{h.catatan.slice(0, 60)}…</span>
                                    : <span className="emr-hist-empty-cell">—</span>}
                              </td>
                              <td className="emr-hist-actions">
                                <button type="button" className="emr-hist-view" title="Lihat detail">
                                  <Eye size={13}/>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {histTab === 'Vital' && (
                <div className="emr-hist-stub">
                  <div className="emr-hist-title"><span>TANDA-TANDA VITAL</span></div>
                  <table className="emr-hist-table">
                    <thead><tr><th>TGL</th><th>TD</th><th>DN</th><th>Suhu</th><th>SpO₂</th><th>BB</th></tr></thead>
                    <tbody>
                      {history.filter((h) => h.vital_signs).length === 0 && (
                        <tr><td colSpan={6} className="emr-hist-empty">Belum ada data vital.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {histTab === 'Alergi' && (
                <div className="emr-hist-stub">
                  <div className="emr-hist-title"><span>ALERGI</span></div>
                  {form.allergy
                    ? <p className="emr-hist-alergi-text">{form.allergy}</p>
                    : <p className="emr-hist-empty">Tidak ada catatan alergi.</p>}
                </div>
              )}

              {!['Riwayat','Vital','Alergi'].includes(histTab) && (
                <div className="emr-hist-stub">
                  <p className="emr-hist-empty">Tab {histTab} belum tersedia.</p>
                </div>
              )}
            </div>
          </div>

        </div>{/* emr-body */}
      </div>{/* emr-shell */}

      <style>{`
        /* ── EMR Shell ─────────────────────────────────────── */
        .emr-backdrop {
          position: fixed; inset: 0; z-index: 1000;
          display: flex; align-items: stretch; justify-content: stretch;
          background: rgba(0,0,0,.45);
        }
        .emr-shell {
          display: flex; flex-direction: column;
          width: 100%; height: 100%;
          background: #f0f4f8;
          overflow: hidden;
          font-size: 13px;
        }

        /* ── Top bar ───────────────────────────────────────── */
        .emr-topbar {
          display: flex; align-items: center; justify-content: space-between;
          background: #1e3a5f; color: #fff;
          padding: 10px 20px; gap: 16px; flex-shrink: 0;
          font-size: 15px;
        }
        .emr-topbar-info { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .emr-topbar-info b { font-weight: 700; color: #93c5fd; margin-right: 3px; }
        .emr-sep { color: #475569; font-size: 18px; }
        .emr-status-badge {
          padding: 3px 12px; border-radius: 5px; font-size: 13px; font-weight: 700;
          background: #22c55e; color: #fff; margin-left: 8px;
        }
        .emr-status-badge.emr-status-final { background: #22c55e; }
        .emr-status-badge.emr-status-draft { background: #f59e0b; }
        .emr-saved-msg {
          font-size: 12px; font-weight: 600; color: #86efac;
          background: rgba(22,163,74,.25); border: 1px solid rgba(22,163,74,.4);
          padding: 5px 12px; border-radius: 6px; white-space: nowrap;
        }
        .emr-topbar-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .emr-btn {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 16px; border-radius: 6px; border: none;
          font-size: 13px; font-weight: 700; cursor: pointer; transition: opacity .15s;
        }
        .emr-btn:disabled { opacity: .55; cursor: not-allowed; }
        .emr-btn-soft    { background: #e2e8f0; color: #1e3a5f; }
        .emr-btn-primary { background: #0369a1; color: #fff; }
        .emr-btn-success { background: #16a34a; color: #fff; }
        .emr-btn-close   { background: #dc2626; color: #fff; }
        .emr-btn-followup { background: #0891b2; color: #fff; }

        /* ── Meta bar ──────────────────────────────────────── */
        .emr-metabar {
          display: flex; align-items: stretch; gap: 6px;
          background: #2d4263;
          padding: 8px 12px;
          flex-shrink: 0; overflow-x: auto;
          border-bottom: 2px solid #1e3a5f;
        }
        .emr-meta-item {
          display: flex; flex-direction: column; gap: 4px;
          padding: 8px 14px;
          background: rgba(255,255,255,0.09);
          border: 1px solid rgba(255,255,255,0.15);
          border-radius: 7px;
          min-width: 0; flex-shrink: 0;
        }
        .emr-meta-lbl {
          font-size: 10px; font-weight: 700; color: #94a3b8;
          letter-spacing: .07em; text-transform: uppercase;
        }
        .emr-meta-val {
          font-size: 13px; font-weight: 600; color: #e2e8f0;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 180px;
        }
        .emr-meta-alergi { color: #fca5a5; }
        .emr-meta-doctor { min-width: 160px; }
        .emr-doctor-select {
          font-size: 13px; color: #e2e8f0; background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2); border-radius: 5px; padding: 3px 6px;
          max-width: 200px;
        }
        .emr-doctor-select option { color: #1e293b; background: #fff; }

        /* ── Alerts ────────────────────────────────────────── */
        .emr-alerts { padding: 6px 12px; background: #f8fafc; flex-shrink: 0; display: flex; flex-direction: column; gap: 4px; }

        /* ── Body split ────────────────────────────────────── */
        .emr-body { display: flex; flex: 1; overflow: hidden; }

        /* ── Left panel ────────────────────────────────────── */
        .emr-form-panel {
          flex: 1; overflow-y: auto; background: #f8fafc;
          padding: 0; display: flex; flex-direction: column;
        }
        .emr-section {
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 16px; background: #fff;
          display: flex; flex-direction: column; gap: 8px;
        }
        .emr-section-last { border-bottom: none; }
        .emr-section-head {
          display: flex; align-items: center; gap: 8px; margin-bottom: 2px;
        }
        .emr-section-label {
          font-size: 11px; font-weight: 800; color: #0369a1;
          text-transform: uppercase; letter-spacing: .06em;
        }
        .emr-badge {
          font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 3px;
          text-transform: uppercase; letter-spacing: .04em;
        }
        .emr-badge-blue   { background: #dbeafe; color: #1d4ed8; }
        .emr-badge-teal   { background: #ccfbf1; color: #0f766e; }
        .emr-badge-orange { background: #ffedd5; color: #c2410c; }
        .emr-placeholder-hint { font-size: 12px; color: #6b7280; margin: 0; font-style: italic; }
        .emr-hint-inline { font-size: 11px; color: #9ca3af; }

        /* Vitals inline */
        .emr-vitals-inline {
          display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
          padding: 8px 10px; background: #f0f9ff; border-radius: 6px;
          border: 1px solid #bae6fd;
        }
        .emr-vital-pair { display: flex; align-items: center; gap: 4px; }
        .emr-vital-pair label { font-size: 11px; font-weight: 700; color: #0369a1; white-space: nowrap; }
        .emr-vital-pair span { font-size: 11px; color: #6b7280; }
        .emr-bmi-val { font-size: 14px; font-weight: 700; color: #0369a1; }

        /* Expand button */
        .emr-expand-btn {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; color: #0369a1; font-weight: 600;
          background: none; border: none; cursor: pointer; padding: 2px 0;
          text-decoration: underline; text-underline-offset: 2px;
        }
        .emr-chevron-open { transform: rotate(180deg); }
        .emr-extra-fields { display: flex; flex-direction: column; gap: 6px; padding-top: 6px; }
        .emr-extra-label { font-size: 11px; font-weight: 600; color: #374151; }
        .emr-exam-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .emr-exam-item { display: flex; flex-direction: column; gap: 3px; }
        .emr-exam-item label { font-size: 11px; font-weight: 600; color: #374151; }

        /* Plan subsections */
        .emr-plan-subsection { display: flex; flex-direction: column; gap: 4px; }
        .emr-plan-sub-label { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: .05em; }
        .emr-add-section-btn {
          font-size: 11px; color: #0369a1; background: none; border: none; cursor: pointer;
          text-decoration: underline; text-underline-offset: 2px; padding: 2px 0; align-self: flex-start;
        }

        /* Follow-up row */
        .emr-followup-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

        /* ── Right panel ───────────────────────────────────── */
        .emr-history-panel {
          width: 400px; flex-shrink: 0; display: flex; flex-direction: column;
          border-left: 2px solid #e2e8f0; background: #fffde7; overflow: hidden;
        }
        .emr-hist-tabs {
          display: flex; flex-wrap: wrap; background: #fff; border-bottom: 1px solid #e2e8f0; flex-shrink: 0;
        }
        .emr-hist-tab {
          padding: 7px 12px; font-size: 12px; font-weight: 500; border: none;
          background: none; cursor: pointer; color: #475569; border-bottom: 2px solid transparent;
          white-space: nowrap;
        }
        .emr-hist-tab:hover { background: #f8fafc; }
        .emr-hist-tab-active { color: #0369a1; border-bottom-color: #0369a1; font-weight: 700; }

        .emr-hist-body { flex: 1; overflow-y: auto; padding: 0; }
        .emr-hist-title {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 12px; background: #fff; border-bottom: 1px solid #e8e2d5;
          font-size: 12px; font-weight: 700; color: #1e3a5f; flex-shrink: 0;
        }
        .emr-hist-print {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 600; color: #475569;
          background: #f1f5f9; border: 1px solid #cbd5e1;
          padding: 3px 9px; border-radius: 4px; cursor: pointer;
        }
        .emr-hist-filter {
          display: flex; align-items: center; gap: 6px;
          padding: 6px 12px; font-size: 12px; color: #374151;
          border-bottom: 1px solid #e8e2d5; background: #fafaf8;
          cursor: pointer; user-select: none;
        }
        .emr-hist-table-wrap { overflow-x: auto; }
        .emr-hist-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .emr-hist-table thead th {
          background: #334155; color: #cbd5e1; font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: .05em;
          padding: 6px 8px; text-align: left; white-space: nowrap;
        }
        .emr-hist-table tbody tr { border-bottom: 1px solid #e2e8f0; }
        .emr-hist-table tbody tr:hover { background: #fef9c3; }
        .emr-hist-row-current { background: #fef3c7 !important; border-left: 3px solid #f59e0b; }
        .emr-hist-date { padding: 6px 8px; color: #374151; font-weight: 500; font-size: 11px; }
        .emr-hist-cell { padding: 6px 8px; color: #374151; vertical-align: top; }
        .emr-hist-empty { text-align: center; color: #9ca3af; padding: 24px; font-style: italic; }
        .emr-hist-empty-cell { color: #9ca3af; }
        .emr-hist-icd { font-size: 10px; background: #dbeafe; color: #1d4ed8; padding: 1px 5px; border-radius: 3px; display: block; margin-bottom: 2px; }
        .emr-hist-tindakan { font-size: 11px; color: #0369a1; }
        .emr-hist-actions { padding: 4px 6px; }
        .emr-hist-view { background: none; border: 1px solid #e2e8f0; border-radius: 4px; padding: 3px 5px; cursor: pointer; color: #64748b; }
        .emr-hist-view:hover { background: #f0f9ff; color: #0369a1; }
        .emr-hist-stub { padding: 12px; }
        .emr-hist-alergi-text { padding: 10px 12px; background: #fee2e2; border-radius: 6px; color: #991b1b; font-size: 13px; }

        /* ── ICD picker (reuse) ────────────────────────────── */
        .icd-picker { display:flex; flex-direction:column; gap:6px; }
        .icd-tags { display:flex; flex-wrap:wrap; gap:5px; }
        .icd-tag { display:inline-flex; align-items:center; gap:5px; background:#dbeafe; color:#1d4ed8; padding:3px 8px; border-radius:5px; font-size:12px; }
        .icd-tag code { font-size:11px; font-weight:700; }
        .icd-tag-remove { background:none; border:none; color:#1d4ed8; cursor:pointer; font-size:14px; line-height:1; padding:0; margin-left:2px; }
        .icd-search-wrap { position:relative; }
        .icd-search-icon { position:absolute; left:9px; top:50%; transform:translateY(-50%); color:#9ca3af; }
        .icd-search-input { width:100%; box-sizing:border-box; padding:7px 10px 7px 28px; border:1px solid #c9cfd8; border-radius:5px; font-size:13px; }
        .icd-dropdown { position:absolute; top:100%; left:0; right:0; z-index:300; background:#fff; border:1px solid #d1d5db; border-radius:7px; box-shadow:0 4px 16px rgba(0,0,0,.12); margin-top:2px; max-height:220px; overflow-y:auto; }
        .icd-option { display:flex; align-items:center; gap:8px; padding:8px 12px; cursor:pointer; font-size:12px; }
        .icd-option:hover { background:#f0f9ff; }
        .icd-option code { font-size:11px; color:#0369a1; font-weight:700; background:#e0f2fe; padding:1px 5px; border-radius:3px; flex-shrink:0; }
        .icd-option span { color:#374151; }
        .icd-clear { font-size:11px; color:#0369a1; background:none; border:none; cursor:pointer; text-decoration:underline; padding:0; text-underline-offset:2px; }
        .icd-required-badge { font-size:10px; background:#fee2e2; color:#dc2626; padding:1px 6px; border-radius:3px; margin-left:4px; }
        .icd-section { display:flex; flex-direction:column; gap:4px; }
        .icd-optional-badge { font-size:10px; background:#f3f4f6; color:#6b7280; padding:1px 6px; border-radius:3px; margin-left:4px; }

        /* ── Prescription (reuse) ──────────────────────────── */
        .rx-builder { display:flex; flex-direction:column; gap:6px; }
        .rx-empty { font-size:12px; color:#9ca3af; margin:0; padding:8px 10px; background:#f8f8f8; border-radius:6px; }
        .rx-row { display:flex; align-items:flex-start; gap:6px; padding:8px; background:#f8f6f2; border-radius:7px; border:1px solid #e8e2d5; }
        .rx-row-num { width:20px; height:20px; border-radius:50%; background:#0369a1; color:#fff; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:5px; }
        .rx-fields { flex:1; display:flex; flex-wrap:wrap; gap:5px; }
        .rx-input { padding:6px 8px; border:1px solid #e8e2d5; border-radius:5px; font-size:12px; background:#fff; }
        .rx-drug-wrap { flex:2; min-width:140px; position:relative; }
        .rx-drug-wrap .rx-input { width:100%; box-sizing:border-box; }
        .rx-drug-dropdown { position:absolute; top:100%; left:0; right:0; z-index:200; background:#fff; border:1px solid #d1d5db; border-radius:7px; box-shadow:0 4px 12px rgba(0,0,0,.12); margin-top:2px; padding:4px 0; list-style:none; max-height:200px; overflow-y:auto; }
        .rx-drug-option { display:flex; align-items:center; gap:8px; padding:7px 10px; cursor:pointer; font-size:12px; }
        .rx-drug-option:hover { background:#f0f9ff; }
        .rx-drug-name { font-weight:500; color:#0c4a6e; }
        .rx-drug-unit { font-size:10px; color:#6b7280; background:#f3f4f6; padding:1px 5px; border-radius:3px; }
        .rx-drug-cat  { font-size:10px; color:#9ca3af; margin-left:auto; }
        .rx-drug  { flex:2; min-width:140px; }
        .rx-dose  { flex:1; min-width:90px; }
        .rx-freq  { flex:1; min-width:80px; }
        .rx-dur   { width:60px; }
        .rx-qty   { width:55px; }
        .rx-route { flex:1; min-width:100px; }
        .rx-notes { flex:2; min-width:140px; }
        .rx-remove { background:none; border:none; color:#c0392b; cursor:pointer; padding:5px; border-radius:5px; flex-shrink:0; margin-top:1px; }
        .rx-remove:hover { background:#fee2e2; }
        .rx-add { margin-top:2px; font-size:12px; }
      `}</style>
    </div>
  );
}
